import React, { useEffect, useState } from 'react'
import { BusinessDetailsProps } from '../interfaces/formDataInterface'
import axiosReq from '../interceptor'
import { useRouter } from 'next/navigation'
import { toast } from 'react-toastify'
import CustomSelect from '../components/CustomSelect/customSelect'
import useDismissAlert from '../Hooks/useDismissAlert'
import { MessageAlert } from '../components/message-alert'

const BUSINESS_SIZES = ['1 - 10', '11 - 50', '51 - 200', '201 - 500', '501 +']

const BusinessDetails: React.FC<BusinessDetailsProps> = ({
  isLoading,
  setIsLoading,
  areFieldsSet,
  onFieldsSet,
  onNext
}) => {
  const [formData, setFormData] = useState({
    business_name: '',
    business_size: '',
    business_category: ''
  })

  const [businessCategories, setBusinessCategories] = useState<
    { id: number; category: string }[]
  >([])
  const [error, setError] = useDismissAlert({ message: '' }, 5000)
  const router = useRouter()
  const email = localStorage.getItem('email')

  useEffect(() => {
    const fetchBusinessCategories = async () => {
      try {
        const { data, status } = await axiosReq.get(
          '/app/v2/business/onboarding/categories'
        )
        if (status === 200) {
          setBusinessCategories(data.categories || [])
          setError({ message: '' })
        } else {
          throw new Error('Unable to load business categories.')
        }
      } catch (error) {
        console.error('Error fetching business categories:', error)
        setError({
          message:
            'Unable to load business categories. Please check your internet connection and try again.'
        })
      }
    }

    fetchBusinessCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleInputChange = (name: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }))

    const allFieldsSet = Object.values({
      ...formData,
      [name]: value
    }).every((field) => field.trim() !== '')

    onFieldsSet(allFieldsSet)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)

    const payload = {
      ...formData,
      email
    }

    try {
      setError({ message: '' })
      const response = await axiosReq.post(
        '/app/v2/auth/sign-up-business-details',
        payload
      )
      console.log('Success:', response.data)
      toast.success(
        'Congratulations! Your business account is now set up. You will be redirected to the login screen shortly.'
      )
      onFieldsSet(true)
      onNext() // Proceed to the next step.
      router.push('/login')
    } catch (error: any) {
      console.error('Error submitting business details:', error)
      setError({
        message:
          error.response.data.message || 'Submission failed! Please try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section>
      <form onSubmit={handleSubmit}>
        <div className="form-outline mb-3">
          <label className="form-label text-white" htmlFor="business_name">
            Business Name <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="business_name"
            name="business_name"
            className="form-control"
            placeholder="Business Name"
            value={formData.business_name}
            onChange={(e) => handleInputChange('business_name', e.target.value)}
            required
          />
        </div>

        <div className="form-outline mb-3">
          <label htmlFor="business_size" className="form-label text-white">
            Business Size <span className="text-danger">*</span>
          </label>
          <CustomSelect
            id="business_size"
            placeholder="Select Size"
            options={BUSINESS_SIZES}
            selected={formData.business_size}
            onSelect={(value) => handleInputChange('business_size', value)}
          />
        </div>

        <div className="form-outline mb-3">
          <label htmlFor="business_category" className="form-label text-white">
            Business Category <span className="text-danger">*</span>
          </label>
          <CustomSelect
            id="business_category"
            placeholder="Select Category"
            options={businessCategories.map((item) => item.category)}
            selected={formData.business_category}
            onSelect={(value) => handleInputChange('business_category', value)}
          />
        </div>

        {error.message && <MessageAlert message={error.message} type="error" />}

        <button
          className="btn btn-primary rounded-pill w-100 mt-3"
          type="submit"
          disabled={isLoading || !areFieldsSet}
        >
          {isLoading ? 'Submitting...' : 'Finish'}
        </button>
      </form>
    </section>
  )
}

export default BusinessDetails
